/**
 * Date-range presets, pay-period grouping and CSV export for the employee
 * hours report.
 *
 * Dates are calendar days (`YYYY-MM-DD`); arithmetic goes through `addDays`
 * from payPeriod.ts, which works at UTC midnight so the operator's own
 * timezone never moves a boundary. "Today" comes from the org's timezone.
 */
import { addDays, payPeriodContaining, type PayCycle } from './payPeriod';
import type { HoursReport, HoursReportDay } from '@/services/timesheet';

export type RangePreset =
  | 'this_period' | 'last_period'
  | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'last_30';

/** `needsCycle` presets only mean something once the employee's pay cycle is known. */
export const RANGE_PRESETS: { key: RangePreset; label: string; needsCycle?: boolean }[] = [
  { key: 'this_period', label: 'This pay period', needsCycle: true },
  { key: 'last_period', label: 'Last pay period', needsCycle: true },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_30', label: 'Last 30 days' },
];

/** Longest range the server will report on. */
export const MAX_RANGE_DAYS = 366;

/** ISO weekday of a calendar date: 1 = Monday … 7 = Sunday. */
export function isoWeekday(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 ? 7 : dow;
}

function lastDayOfMonth(y: number, m: number): string {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/**
 * Resolve a preset against `today` (the org's today, YYYY-MM-DD). Weeks run
 * Monday–Sunday. A pay-period preset with no cycle falls back to the week.
 */
export function presetRange(
  preset: RangePreset,
  today: string,
  cycle?: PayCycle | null,
): { start: string; end: string } {
  const [y, m] = today.split('-').map(Number);
  const monday = addDays(today, 1 - isoWeekday(today));
  switch (preset) {
    case 'this_period': {
      if (!cycle) return presetRange('this_week', today);
      const p = payPeriodContaining(today, cycle);
      return { start: p.startDate, end: p.endDate };
    }
    case 'last_period': {
      if (!cycle) return presetRange('last_week', today);
      const p = payPeriodContaining(addDays(today, -cycle.width), cycle);
      return { start: p.startDate, end: p.endDate };
    }
    case 'this_week':
      return { start: monday, end: addDays(monday, 6) };
    case 'last_week':
      return { start: addDays(monday, -7), end: addDays(monday, -1) };
    case 'this_month':
      return { start: `${today.slice(0, 7)}-01`, end: lastDayOfMonth(y, m) };
    case 'last_month': {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { start: `${py}-${String(pm).padStart(2, '0')}-01`, end: lastDayOfMonth(py, pm) };
    }
    case 'last_30':
      return { start: addDays(today, -29), end: today };
  }
}

/** Which preset (if any) a range corresponds to, so its chip can show as selected. */
export function matchPreset(
  start: string,
  end: string,
  today: string,
  cycle?: PayCycle | null,
): RangePreset | null {
  for (const { key, needsCycle } of RANGE_PRESETS) {
    if (needsCycle && !cycle) continue;
    const r = presetRange(key, today, cycle);
    if (r.start === start && r.end === end) return key;
  }
  return null;
}

/** One pay period's share of a report: its days, and what they add up to. */
export interface HoursReportPeriod {
  /** The whole pay period, which may run past the report's own range. */
  start: string;
  end: string;
  /** The report covers only part of this period, so its totals are not the pay run's. */
  partial: boolean;
  days: HoursReportDay[];
  scheduled_hours: number;
  total_hours: number;
  worked_hours: number;
  leave_hours: number;
  holiday_hours: number;
  variance_hours: number;
  by_category: Record<string, number>;
  /** Hours on an approved submission. Leave and time in lieu are added on these alone. */
  approved_hours: number;
  /**
   * What approving this period's hours added to the employee's balances, one
   * line per balance: the server's record, plus an estimate for any hours
   * approved before a record was kept. Null when the server gave neither.
   */
  added: { label: string; hours: number }[] | null;
  /** Approved hours whose share of `added` is an estimate at today's rates, not a record. */
  estimated_hours: number;
}

// The order balances are listed in; anything else follows in the order it turns up.
const ADDED_ORDER = ['Annual Leave', 'Personal/Sick Leave', 'Long Service Leave', 'Time in Lieu'];

/**
 * The report's days split into the pay periods they fall in, in date order,
 * each with its own subtotals and what its approved hours added to balances.
 */
export function groupByPayPeriod(report: HoursReport, cycle: PayCycle): HoursReportPeriod[] {
  const periods: HoursReportPeriod[] = [];
  // Approved hours with no record of what they added, and their time in lieu.
  const unrecorded = new Map<HoursReportPeriod, { hours: number; til: number }>();
  for (const d of report.days) {
    const { startDate, endDate } = payPeriodContaining(d.date, cycle);
    let p = periods[periods.length - 1];
    if (!p || p.start !== startDate) {
      p = {
        start: startDate,
        end: endDate,
        partial: report.start_date > startDate || report.end_date < endDate,
        days: [],
        scheduled_hours: 0,
        total_hours: 0,
        worked_hours: 0,
        leave_hours: 0,
        holiday_hours: 0,
        variance_hours: 0,
        by_category: {},
        approved_hours: 0,
        added: null,
        estimated_hours: 0,
      };
      periods.push(p);
    }
    p.days.push(d);
    p.scheduled_hours += d.scheduled_hours;
    p.total_hours += d.total_hours;
    p.worked_hours += d.worked_hours;
    p.leave_hours += d.leave_hours;
    p.holiday_hours += d.holiday_hours;
    p.variance_hours += d.variance_hours;
    p.approved_hours += d.approved_hours ?? 0;
    // A server that keeps no record sends no unrecorded_* figures: every
    // approved hour is then unrecorded.
    const u = unrecorded.get(p) ?? { hours: 0, til: 0 };
    u.hours += d.unrecorded_approved_hours ?? d.approved_hours ?? 0;
    u.til += d.unrecorded_approved_til_hours ?? d.approved_til_hours ?? 0;
    unrecorded.set(p, u);
    for (const [category, hours] of Object.entries(d.by_category)) {
      p.by_category[category] = (p.by_category[category] ?? 0) + hours;
    }
  }

  const { accrual, accruals } = report;
  if (!accrual && !accruals) return periods;

  const lines = new Map<HoursReportPeriod, Map<string, number>>(periods.map(p => [p, new Map()]));
  const add = (p: HoursReportPeriod, label: string, hours: number) => {
    const m = lines.get(p)!;
    m.set(label, (m.get(label) ?? 0) + hours);
  };

  // The record. An approval normally covers exactly one pay period; one that
  // does not is counted once, in the period it starts in, or failing that the
  // first period on the report that it reaches.
  for (const a of accruals ?? []) {
    const p = periods.find(x => x.start <= a.period_start && a.period_start <= x.end)
      ?? periods.find(x => x.start <= a.period_end && a.period_start <= x.end);
    if (p) add(p, a.label, a.hours);
  }

  // The estimate, only for hours approved before a record was kept. Approval
  // accrues leave on every hour of the submission, leave taken and public
  // holidays included, so the rate applies to all of them.
  if (accrual) {
    for (const p of periods) {
      const u = unrecorded.get(p)!;
      if (u.hours < 0.005) continue;
      p.estimated_hours = u.hours;
      for (const l of accrual.leave) add(p, l.label, u.hours * l.hours_per_hour);
      if (accrual.time_in_lieu) add(p, 'Time in Lieu', u.til);
    }
  }

  const rank = (label: string) => {
    const i = ADDED_ORDER.indexOf(label);
    return i === -1 ? ADDED_ORDER.length : i;
  };
  for (const p of periods) {
    p.added = [...lines.get(p)!]
      .map(([label, hours]) => ({ label, hours }))
      // A line that rounds to nothing is not something that was added.
      .filter(l => l.hours >= 0.005)
      .sort((a, b) => rank(a.label) - rank(b.label));
  }
  return periods;
}

const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * The report as CSV: one row per day with a column per payroll type, then a
 * totals row. Times are passed in pre-formatted so the file shows the org's
 * local times, the same as the screen.
 *
 * With a pay cycle, each row leads with the pay period it belongs to. Period
 * subtotals are left out on purpose: a subtotal row in the middle of the data
 * is counted twice by anyone who sums a column.
 */
export function hoursReportCsv(
  report: HoursReport,
  formatTime: (iso: string) => string,
  cycle?: PayCycle | null,
): string {
  const cats = report.categories.map(c => c.category);
  const header = ['Date', 'Day', 'Rostered', 'First in', 'Last out', ...cats, 'Total', 'Variance', 'Notes'];
  const DAY = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const rows = report.days.map(d => {
    const notes = [
      d.holiday_name,
      d.open_entries ? `${d.open_entries} open clock-in` : null,
      d.missed_clock_entries ? `${d.missed_clock_entries} missed clock` : null,
      d.edited_entries ? `${d.edited_entries} edited` : null,
    ].filter(Boolean).join('; ');
    return [
      d.date,
      DAY[d.weekday],
      d.scheduled_hours.toFixed(2),
      d.first_in ? formatTime(d.first_in) : '',
      d.last_out ? formatTime(d.last_out) : '',
      ...cats.map(c => (d.by_category[c] ?? 0).toFixed(2)),
      d.total_hours.toFixed(2),
      d.variance_hours.toFixed(2),
      notes,
    ];
  });
  const t = report.totals;
  const totals = [
    'Total', '', t.scheduled_hours.toFixed(2), '', '',
    ...report.categories.map(c => c.hours.toFixed(2)),
    t.total_hours.toFixed(2), t.variance_hours.toFixed(2), '',
  ];
  if (cycle) {
    header.unshift('Pay period');
    rows.forEach((row, i) => {
      const p = payPeriodContaining(report.days[i].date, cycle);
      row.unshift(`${p.startDate} to ${p.endDate}`);
    });
    totals.splice(1, 0, '');
  }
  return [header, ...rows, totals].map(r => r.map(csvCell).join(',')).join('\n');
}
